if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.elements.viewer?.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          const primeSelectedMedia = () => this.primeMedia(mediaToSwitch.dataset.target, true);
          button.addEventListener('pointerenter', primeSelectedMedia, { once: true });
          button.addEventListener('focus', primeSelectedMedia, { once: true });
          button.addEventListener('pointerdown', primeSelectedMedia, { once: true });
          button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.thumbnailList = this.elements.thumbnails?.querySelector('[id^="Slider-Thumbnails"]');
        this.querySelectorAll('[data-thumbnail-scroll]').forEach((control) => {
          control.addEventListener('click', () => this.scrollThumbnailRail(control.dataset.thumbnailScroll));
        });
        this.thumbnailList?.addEventListener('scroll', debounce(this.updateThumbnailControls.bind(this), 80));
        if (this.thumbnailList && 'ResizeObserver' in window) {
          this.thumbnailResizeObserver = new ResizeObserver(() => this.updateThumbnailControls());
          this.thumbnailResizeObserver.observe(this.thumbnailList);
        }
        this.elements.viewer?.addEventListener('keydown', this.onKeydown.bind(this));
        if (this.elements.viewer) this.elements.viewer.tabIndex = 0;
        if (this.dataset.desktopDrag === 'true') this.enableDesktopDrag();
        if (this.elements.thumbnails && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
        this.updateThumbnailControls();
        window.requestAnimationFrame(() => this.updateThumbnailControls());
        this.scheduleGalleryWarmup();
      }

      disconnectedCallback() {
        this.thumbnailResizeObserver?.disconnect();
      }

      get mediaItems() {
        return Array.from(this.elements.viewer?.querySelectorAll('li.product__media-item[data-media-id]') || []);
      }

      getMediaImage(mediaId) {
        const mediaItem = this.elements.viewer?.querySelector(
          `li.product__media-item[data-media-id="${mediaId}"]`
        );
        return mediaItem?.querySelector('img');
      }

      primeMedia(mediaId, urgent = false) {
        const image = this.getMediaImage(mediaId);
        if (!image || (image.complete && image.naturalWidth)) return Promise.resolve();

        image.loading = 'eager';
        image.fetchPriority = urgent ? 'high' : 'low';

        if (typeof image.decode === 'function') {
          return image.decode().catch(() => {});
        }

        return new Promise((resolve) => {
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', resolve, { once: true });
        });
      }

      scheduleGalleryWarmup() {
        const items = this.mediaItems;
        if (items.length < 2) return;

        const activeIndex = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));
        const nearbyItems = [items[activeIndex + 1], items[activeIndex - 1]].filter(Boolean);
        const remainingItems = items.filter(
          (item, index) => index !== activeIndex && !nearbyItems.includes(item)
        );
        const preloadOrder = [...nearbyItems, ...remainingItems];

        const warmGallery = async () => {
          for (let index = 0; index < preloadOrder.length; index += 1) {
            if (!this.isConnected) return;
            await this.primeMedia(preloadOrder[index].dataset.mediaId, index === 0);
          }
        };

        const beginWarmup = () => window.setTimeout(warmGallery, 150);
        const activeImage = items[activeIndex]?.querySelector('img');

        if (!activeImage || (activeImage.complete && activeImage.naturalWidth)) {
          beginWarmup();
          return;
        }

        if (typeof activeImage.decode === 'function') {
          activeImage.decode().catch(() => {}).finally(beginWarmup);
        } else {
          activeImage.addEventListener('load', beginWarmup, { once: true });
          activeImage.addEventListener('error', beginWarmup, { once: true });
        }
      }

      stepMedia(direction) {
        const items = this.mediaItems;
        if (items.length < 2) return;
        const activeIndex = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));
        const nextIndex = direction === 'previous'
          ? Math.max(0, activeIndex - 1)
          : Math.min(items.length - 1, activeIndex + 1);
        if (nextIndex === activeIndex) return;
        this.setActiveMedia(items[nextIndex].dataset.mediaId, false);
      }

      scrollThumbnailRail(direction) {
        if (!this.thumbnailList) return;
        const firstThumbnail = this.thumbnailList.querySelector('.thumbnail-list__item');
        const listStyles = window.getComputedStyle(this.thumbnailList);
        const gap = Number.parseFloat(listStyles.columnGap || listStyles.gap) || 0;
        const minimumStep = (firstThumbnail?.getBoundingClientRect().width || 0) + gap;
        const scrollStep = Math.max(minimumStep, this.thumbnailList.clientWidth * 0.75);
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.thumbnailList.scrollBy({
          left: direction === 'previous' ? -scrollStep : scrollStep,
          behavior: reducedMotion ? 'auto' : 'smooth',
        });
        window.setTimeout(() => this.updateThumbnailControls(), reducedMotion ? 0 : 260);
      }

      updateThumbnailControls() {
        if (!this.thumbnailList) return;
        const maximumScroll = Math.max(0, this.thumbnailList.scrollWidth - this.thumbnailList.clientWidth);
        const atStart = maximumScroll <= 1 || this.thumbnailList.scrollLeft <= 1;
        const atEnd = maximumScroll <= 1 || this.thumbnailList.scrollLeft >= maximumScroll - 1;
        this.elements.thumbnails?.toggleAttribute('data-thumbnails-at-start', atStart);
        this.elements.thumbnails?.toggleAttribute('data-thumbnails-at-end', atEnd);
        this.querySelectorAll('[data-thumbnail-scroll="previous"]').forEach((button) => {
          button.disabled = atStart;
        });
        this.querySelectorAll('[data-thumbnail-scroll="next"]').forEach((button) => {
          button.disabled = atEnd;
        });

        const thumbnails = Array.from(this.thumbnailList.querySelectorAll('.thumbnail-list__item')).filter(
          (thumbnail) => !thumbnail.hidden && window.getComputedStyle(thumbnail).display !== 'none'
        );
        const range = this.querySelector('[data-thumbnail-range]');
        if (!range || thumbnails.length === 0) return;

        const listRect = this.thumbnailList.getBoundingClientRect();
        const fullyVisibleIndexes = thumbnails.reduce((indexes, thumbnail, index) => {
          const rect = thumbnail.getBoundingClientRect();
          if (rect.left >= listRect.left - 1 && rect.right <= listRect.right + 1) indexes.push(index);
          return indexes;
        }, []);
        const visibleIndexes = fullyVisibleIndexes.length > 0
          ? fullyVisibleIndexes
          : thumbnails.reduce((indexes, thumbnail, index) => {
              const rect = thumbnail.getBoundingClientRect();
              if (rect.right > listRect.left + 1 && rect.left < listRect.right - 1) indexes.push(index);
              return indexes;
            }, []);
        const firstVisible = visibleIndexes[0] ?? 0;
        const lastVisible = visibleIndexes.at(-1) ?? firstVisible;
        range.textContent = maximumScroll <= 1
          ? `${thumbnails.length} photos`
          : `${firstVisible + 1}\u2013${lastVisible + 1} of ${thumbnails.length}`;
      }

      onKeydown(event) {
        if (!this.mql.matches || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.stepMedia(event.key === 'ArrowLeft' ? 'previous' : 'next');
      }

      enableDesktopDrag() {
        let startX = null;
        let dragged = false;
        this.elements.viewer?.addEventListener('pointerdown', (event) => {
          if (!this.mql.matches || event.button !== 0 || event.target.closest('video, iframe, model-viewer')) return;
          startX = event.clientX;
          dragged = false;
        });
        this.elements.viewer?.addEventListener('pointermove', (event) => {
          if (startX === null || Math.abs(event.clientX - startX) < 24) return;
          dragged = true;
        });
        this.elements.viewer?.addEventListener('pointerup', (event) => {
          if (startX === null) return;
          const distance = event.clientX - startX;
          startX = null;
          if (!dragged || Math.abs(distance) < 48) return;
          event.preventDefault();
          this.stepMedia(distance > 0 ? 'previous' : 'next');
        });
        this.elements.viewer?.addEventListener('click', (event) => {
          if (!dragged) return;
          event.preventDefault();
          event.stopPropagation();
          dragged = false;
        }, true);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`li.product__media-item[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('li.product__media-item[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('li.product__media-item[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        window.setTimeout(() => this.updateThumbnailControls(), 0);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
